/**
 * App shell — header banner + tab switcher.
 *
 * Two top-level modes (Issue #20):
 *
 *   - **`?mode=business`** — Business Demo skin. Wizard-form для бизнес-
 *     заказчиков и нетехнических стейкхолдеров. Скрывает Endpoint Explorer
 *     / Failure-Mode / HMAC Validator / raw JSON. Доступ к dev режиму
 *     через явную кнопку «Режим разработчика».
 *
 *   - **dev mode (default)** — engineer dev tool. Four tabs (Endpoint
 *     Explorer / Webhook Timeline / Scenario Runner / Failure Modes).
 *     Tab selection lives в `window.location.hash`.
 *
 * Mode переключатель = URL `?mode=business` query param — позволяет shareable
 * links + page reload preserves state. Mini-router < 50 lines: no
 * react-router dep oversight для MVP.
 */
import { useEffect, useState } from 'react';
import { HeaderBanner } from './components/HeaderBanner';
import { BusinessDemo } from './pages/BusinessDemo';
import { EndpointExplorer } from './pages/EndpointExplorer';
import { FailureModePanel } from './pages/FailureModePanel';
import { ScenarioRunner } from './pages/ScenarioRunner';
import { WebhookTimeline } from './pages/WebhookTimeline';
import { cn } from './lib/cn';

type Tab = 'explorer' | 'webhooks' | 'scenarios' | 'failures';
type Mode = 'dev' | 'business';

const TAB_HASH_PREFIX = '#tab:';
const MODE_PARAM = 'mode';
const BUSINESS_MODE_VALUE = 'business';

function readModeFromSearch(): Mode {
  const params = new URLSearchParams(window.location.search);
  return params.get(MODE_PARAM) === BUSINESS_MODE_VALUE ? 'business' : 'dev';
}

function setModeInSearch(next: Mode): void {
  const url = new URL(window.location.href);
  if (next === 'business') {
    url.searchParams.set(MODE_PARAM, BUSINESS_MODE_VALUE);
  } else {
    url.searchParams.delete(MODE_PARAM);
  }
  url.hash = '';
  window.history.pushState({}, '', url.toString());
}

function readTabFromHash(): Tab {
  const raw = window.location.hash;
  if (raw.startsWith(`${TAB_HASH_PREFIX}webhooks`)) return 'webhooks';
  if (raw.startsWith(`${TAB_HASH_PREFIX}scenarios`)) return 'scenarios';
  if (raw.startsWith(`${TAB_HASH_PREFIX}failures`)) return 'failures';
  return 'explorer';
}

export function App() {
  const [mode, setModeState] = useState<Mode>(() => readModeFromSearch());
  const [tab, setTab] = useState<Tab>(() => readTabFromHash());

  useEffect(() => {
    const onHash = () => setTab(readTabFromHash());
    const onPop = () => {
      setModeState(readModeFromSearch());
      setTab(readTabFromHash());
    };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  const switchMode = (next: Mode) => {
    setModeState(next);
    setModeInSearch(next);
  };

  const select = (next: Tab) => {
    setTab(next);
    if (next === 'explorer') {
      const cleaned = window.location.hash.replace(/^#tab:[^/]*\/?/, '#');
      window.location.hash = cleaned === '#' ? '' : cleaned;
    } else {
      window.location.hash = `${TAB_HASH_PREFIX}${next}`;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <HeaderBanner mode={mode} onSwitchMode={switchMode} />
      {mode === 'dev' && (
        <nav className="px-6 border-b border-slate-200 bg-white flex gap-1">
          <TabButton active={tab === 'explorer'} onClick={() => select('explorer')}>
            Endpoint Explorer
          </TabButton>
          <TabButton active={tab === 'webhooks'} onClick={() => select('webhooks')}>
            Webhook Timeline
          </TabButton>
          <TabButton active={tab === 'scenarios'} onClick={() => select('scenarios')}>
            Scenario Runner
          </TabButton>
          <TabButton active={tab === 'failures'} onClick={() => select('failures')}>
            Failure Modes
          </TabButton>
        </nav>
      )}
      <div className="flex-1 min-h-0">
        {mode === 'business' && <BusinessDemo />}
        {mode === 'dev' && tab === 'explorer' && <EndpointExplorer />}
        {mode === 'dev' && tab === 'webhooks' && <WebhookTimeline />}
        {mode === 'dev' && tab === 'scenarios' && <ScenarioRunner />}
        {mode === 'dev' && tab === 'failures' && <FailureModePanel />}
      </div>
    </div>
  );
}

interface TabButtonProps {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}

function TabButton({ active, onClick, children }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm border-b-2 -mb-px transition-colors',
        active
          ? 'border-brand text-slate-900 font-medium'
          : 'border-transparent text-slate-500 hover:text-slate-700',
      )}
    >
      {children}
    </button>
  );
}
